import { useEffect, useRef, useState } from 'react';

import CloseButton from '@/components/Buttons/CloseButton/CloseButton';
import LoginModal from '@/components/LoginModal/LoginModal';
import SVGInstagramIcon from '@/components/svgs/SVGInstagramIcon';
import SVGTikTokIcon from '@/components/svgs/SVGTikTokIcon';
import type { InspoImportEntrySource } from '@/contexts/InspoImportContext';
import { useSocialImportUrlSubmit } from '@/hooks/useSocialImportUrlSubmit';
import { useIsMobileViewport } from '@/hooks/useIsMobileViewport';
import { WebAnalytics } from '@/lib/analytics';
import { EVENT_INSPO_IMPORT_FLOW_CANCELLED } from '@/lib/analyticsEvents';
import { resolveInspoImportEntryChannel } from '@/utils/inspoImportEntryChannel';

export interface TripImportSocialInputStepProps {
  prefilledUrl?: string;
  entrySource?: InspoImportEntrySource;
  isOnboardingEmbed?: boolean;
  parseErrorMessage?: string | null;
  autoStartFromPrefilled?: boolean;
  onClose: () => void;
  onImport: (normalizedUrl: string) => void;
}

export function TripImportSocialInputStep({
  prefilledUrl = '',
  entrySource = 'in_app',
  isOnboardingEmbed = false,
  parseErrorMessage = null,
  autoStartFromPrefilled = false,
  onClose,
  onImport,
}: TripImportSocialInputStepProps) {
  const isMobileViewport = useIsMobileViewport();
  const [inputUrl, setInputUrl] = useState(prefilledUrl);
  const processedAutoRef = useRef<string | null>(null);
  const usedManualPasteSubmitRef = useRef(false);

  const {
    urlValidationError,
    setUrlValidationError,
    submitUrl,
    showLoginModal,
    setShowLoginModal,
    loginModalRedirectPath,
  } = useSocialImportUrlSubmit({
    entrySource,
    isOnboardingEmbed,
    onUrlAccepted: onImport,
  });

  const displayError = urlValidationError || parseErrorMessage || '';
  const canImport = Boolean(inputUrl.trim());

  useEffect(() => {
    setInputUrl(prefilledUrl);
  }, [prefilledUrl]);

  useEffect(() => {
    if (!autoStartFromPrefilled) return;
    const trimmed = prefilledUrl.trim();
    if (!trimmed || processedAutoRef.current === trimmed) return;
    processedAutoRef.current = trimmed;
    submitUrl(trimmed, 'auto');
  }, [autoStartFromPrefilled, prefilledUrl, submitUrl]);

  const horizontalPad = isMobileViewport ? 'px-5' : 'px-8';
  const headerPadTop = isMobileViewport ? 'pt-5' : 'pt-7';
  const footerPadBottom = isMobileViewport
    ? 'pb-[calc(1.25rem+env(safe-area-inset-bottom))]'
    : 'pb-5';

  const handleCancel = () => {
    WebAnalytics.track(EVENT_INSPO_IMPORT_FLOW_CANCELLED, {
      screen: 'idle',
      had_places: false,
      entry_channel: resolveInspoImportEntryChannel(entrySource, usedManualPasteSubmitRef.current),
    });
    onClose();
  };

  return (
    <>
      <div className="trip-import-social-input-step flex flex-col overflow-hidden">
        <header
          className={`flex shrink-0 items-start justify-between gap-4 ${horizontalPad} ${headerPadTop} pr-6`}
        >
          <div className="flex min-w-0 flex-1 flex-col gap-1.5 text-[#252528]">
            <h1 className="font-lora text-[22px] font-semibold leading-[28px] tracking-[0.3px] min-[768px]:text-[26px] min-[768px]:leading-[normal]">
              Import from social media
            </h1>
            <p className="font-manrope text-[14px] font-normal leading-5 tracking-[0.28px] text-[#525257]">
              Paste a Reel or TikTok. We&apos;ll find every place, map it, and make it bookable.
            </p>
          </div>
          <CloseButton
            onClick={handleCancel}
            ariaLabel="Close import"
            size={16}
            className="!flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#f3f3f3] text-[#252528]"
          />
        </header>

        <div
          className={`gt-keyboard-scroll flex flex-col gap-2 overflow-y-auto ${horizontalPad} py-6 ${
            isMobileViewport ? 'min-h-0 flex-1' : ''
          }`}
        >
          <div
            className={`flex h-[50px] items-center gap-3 rounded-[20px] border bg-white px-4 shadow-[0_0_10px_rgba(0,0,0,0.15)] ${
              displayError ? 'border-[#e04710]' : 'border-[#a7a7ae]'
            }`}
          >
            <span className="flex shrink-0 items-center gap-1.5" aria-hidden>
              <SVGInstagramIcon size={24} />
              <SVGTikTokIcon size={22} />
            </span>
            <input
              id="trip-import-social-link"
              type="url"
              inputMode="url"
              aria-label="Instagram or TikTok post URL"
              aria-invalid={Boolean(displayError)}
              value={inputUrl}
              onChange={(event) => {
                setInputUrl(event.target.value);
                if (urlValidationError) setUrlValidationError('');
              }}
              placeholder="Paste link here"
              className="min-w-0 flex-1 border-0 bg-transparent font-manrope text-[16px] font-semibold leading-[22px] tracking-[0.32px] text-[#525257] outline-none ring-0 placeholder:text-[#a7a7ae]"
              autoCapitalize="none"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
            />
          </div>
          {displayError ? (
            <p role="alert" className="font-manrope text-[14px] leading-5 text-[#e04710]">
              {displayError}
            </p>
          ) : (
            <p className="font-manrope text-[12px] font-normal leading-4 tracking-[0.24px] text-[#525257]">
              Works best when the caption names the place.
            </p>
          )}
        </div>

        <footer
          className={`flex shrink-0 items-center justify-end gap-3 ${horizontalPad} ${footerPadBottom} pt-2`}
        >
          <button
            type="button"
            className="inline-flex h-11 items-center justify-center rounded-full px-6 font-manrope text-[16px] font-semibold leading-[22px] tracking-[0.32px] text-[#525257] hover:bg-[#f3f3f3]"
            onClick={handleCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!canImport}
            className="inline-flex h-11 items-center justify-center rounded-full bg-[#252528] px-6 font-manrope text-[16px] font-semibold leading-[22px] tracking-[0.32px] text-white hover:bg-[#3a3a3d] disabled:cursor-not-allowed disabled:opacity-30"
            onClick={() => {
              usedManualPasteSubmitRef.current = true;
              submitUrl(inputUrl, 'manual');
            }}
          >
            Import
          </button>
        </footer>
      </div>

      {showLoginModal ? (
        <LoginModal
          onClose={() => setShowLoginModal(false)}
          redirectPath={loginModalRedirectPath || '/'}
          onAuthenticated={() => setShowLoginModal(false)}
        />
      ) : null}
    </>
  );
}
